import {
	defaultAttachmentTypeLabelMap,
	type FileClassificationStatus,
	type FileId,
	type FileType,
} from "@inrev/common";
import { FileUploadStatus, resolveFileUploadStatus } from "@portal/api/files";
import type { FC } from "react";
import { trackAttachmentAction } from "../../../../analytics";
import { FileRow } from "../../../patterns/file-row";
import { Tag } from "../../../ui/Tag";
import { FileUploadStatusIndicator } from "../../file-upload";

export type DocumentFileStatus =
	| "uploading"
	| "classifying"
	| "classified"
	| "uploadFailed"
	| "classificationFailed";

export type DocumentFile = {
	id: FileId;
	name: string;
	types: FileType[];
	status: DocumentFileStatus;
	/** The server-side classification status, used to resolve the correct status chrome. */
	classificationStatus?: FileClassificationStatus;
};

type DocumentFileRowProps = {
	file: DocumentFile;
	onRemove: (id: FileId) => void;
	trackingId: string;
};

export const DocumentFileRow: FC<DocumentFileRowProps> = ({ file, onRemove, trackingId }) => {
	const status = resolveFileUploadStatus({
		uploading: file.status === "uploading",
		uploadFailed: file.status === "uploadFailed",
		classificationStatus: file.classificationStatus,
		hasTypes: file.types.length > 0,
		typesConfirmedByAdmin: file.status === "classified",
	});
	const failed = status === FileUploadStatus.uploadFailed;

	return (
		<FileRow
			name={file.name}
			dataStatus={file.status}
			danger={failed}
			muted={file.status === "uploading"}
			status={
				<>
					<FileUploadStatusIndicator status={status} id={file.id} />
					{status === FileUploadStatus.ready && file.types.length > 0 && (
						<span className="inline-flex items-center gap-1.5 animate-file-tag-pop-in">
							{file.types.map((type) => (
								<Tag key={type} color="gray" border className="px-2 py-0.5 text-[11px]">
									{defaultAttachmentTypeLabelMap[type] ?? type}
								</Tag>
							))}
						</span>
					)}
				</>
			}
			onRemove={() => {
				trackAttachmentAction("delete", "submission-upload");
				onRemove(file.id);
			}}
			trackingId={trackingId}
		/>
	);
};
